import { Account } from '@citrite/sf-api';
import {
	ClientPostRequest,
	Principal,
	PrincipalType,
	PrivateUserGroupMetadata,
	PublicUserGroupMetadata,
	User,
	UserGroupVisibility,
	UserKind,
	UserMetadata,
	UserRole,
} from '@sharefiledev/sharing-pilet-sdk/api';
import { getUserRID } from '../AppContext';
import { PermissionsHttp } from '../PermissionsHttp';

export type UserGroupMetadata = PrivateUserGroupMetadata | PublicUserGroupMetadata;

/**
 * Checks if the given principal is active. Assumes a UserGroup is always active.
 *
 * @param principal - The principal to check.
 * @returns `true` if the principal is active, otherwise `false`.
 */
export const isPrincipalActive = (principal: Principal): boolean => {
	if (principal.type === PrincipalType.Group) {
		return true;
	}
	return (principal.metadata as UserMetadata).deleted !== true;
};

/**
 * Maps a user to a principal object of User type.
 *
 * @param account - The account object associated with the user.
 * @param user - The user object to be mapped.
 * @returns The principal object containing user information.
 */
export const mapUserToPrincipal = (account: Account, user: User): Principal => ({
	// empty string is intentionally add for user which is not yet created in the system
	rid: user.userId ? getUserRID(account, user.userId) : '',
	type: PrincipalType.User,
	metadata: {
		active: user.active,
		verified: user.verified,
		deleted: user.deleted,
		email: user.email,
		firstName: user.firstName,
		lastName: user.lastName,
		userRoles: user.roles,
	},
});

/**
 * Maps a Principal object to a User object.
 *
 * @param principal - The Principal object to be mapped.
 * @returns The corresponding User object.
 */
export const mapPrincipalOfUserTypeToUser = (principal: Principal): User => {
	const { active, email, firstName, lastName, userRoles } =
		principal.metadata as UserMetadata;
	return {
		userId: getUserIDfromUserRID(principal.rid),
		active,
		email,
		firstName,
		lastName,
		roles: userRoles,
	};
};

/**
 * Extracts the user ID from a given user RID.
 *
 * @param userRID - The user RID string.
 * @returns The extracted user ID, or an empty string if the RID is invalid.
 */
export const getUserIDfromUserRID = (userRID: string): string => {
	const userPrefix = 'user-';
	if (!userRID) {
		return '';
	}
	const startIndex = userRID.indexOf(userPrefix);
	return startIndex !== -1 ? userRID.substring(startIndex + userPrefix.length) : '';
};

// Helper function to map UserKind to UserRole
const mapUserKindToRole = (userKind: string): UserRole => {
	switch (userKind.toLowerCase()) {
		case UserKind.Licensed.toLowerCase():
			return UserRole.Employee;
		case UserKind.Unlicensed.toLowerCase():
			return UserRole.Client;
		default:
			return UserRole.Client;
	}
};

/**
 * Retrieves the role of a given principal. Maps UserKind to UserRole for a UserGroup principal.
 *
 * @param principal - The principal whose role is to be determined.
 * @returns The role of the principal as a `UserRole`.
 */
export const getUserRoleForPrincipal = (principal: Principal): UserRole => {
	switch (principal.type) {
		case PrincipalType.User:
			return (principal.metadata as UserMetadata).userRoles?.[0] ?? UserRole.Client;
		case PrincipalType.Group:
			const userKind =
				(principal.metadata as UserGroupMetadata).userKind ?? UserKind.Unlicensed;
			return mapUserKindToRole(userKind);
		default:
			return UserRole.Client;
	}
};

/**
 * Checks if a principal fulfills the specified user roles. Maps UserKind to UserRole for a UserGroup principal.
 *
 * @param principal - The principal to be checked.
 * @param roles - The array of user roles to check against.
 * @returns `true` if the principal fulfills all the specified roles, otherwise `false`.
 */
export const doesPrincipalFulfillUserRoles = (
	principal: Principal,
	roles: UserRole[]
): boolean => {
	switch (principal.type) {
		case PrincipalType.User:
			return roles.every(role =>
				(principal.metadata as UserMetadata).userRoles?.includes(role)
			);
		case PrincipalType.Group:
			const userKind =
				(principal.metadata as UserGroupMetadata).userKind ?? UserKind.Unlicensed;
			return roles.every(role => mapUserKindToRole(userKind) === role);
		default:
			return false;
	}
};

/**
 * Determines if the given object is of User type by checking for the presence of an 'email' property.
 *
 * @param userOrPrincipal - The object to check, which can be of type User or Principal.
 * @returns A boolean indicating whether the object is a User and not Principal type.
 */
export const isUserType = (userOrPrincipal: User | Principal): boolean => {
	return 'email' in userOrPrincipal;
};

export const isMatchingUserMetadata = (
	metadata: UserMetadata,
	email: string,
	firstName: string,
	lastName: string
): boolean => {
	return (
		metadata.email === email &&
		metadata.firstName === firstName &&
		metadata.lastName === lastName
	);
};

export type UserPrincipal = Omit<Principal, 'type' | 'metadata'> & {
	type: PrincipalType.User;
	metadata: UserMetadata;
};

export type UserGroupPrincipal = Omit<Principal, 'type' | 'metadata'> & {
	type: PrincipalType.Group;
	metadata: UserGroupMetadata;
};

export type PrivateUserGroupPrincipal = Omit<Principal, 'type' | 'metadata'> & {
	type: PrincipalType.Group;
	metadata: PrivateUserGroupMetadata;
};

export type PublicUserGroupPrincipal = Omit<Principal, 'type' | 'metadata'> & {
	type: PrincipalType.Group;
	metadata: PublicUserGroupMetadata;
};

export function isUserPrincipal(principal: Principal): principal is UserPrincipal {
	return principal.type === PrincipalType.User;
}

export function isUserGroupPrincipal(
	principal: Principal
): principal is UserGroupPrincipal {
	return principal.type === PrincipalType.Group;
}

export function isPrivateUserGroupPrincipal(
	principal: Principal
): principal is PrivateUserGroupPrincipal {
	return (
		isUserGroupPrincipal(principal) &&
		principal.metadata.visibility?.toLocaleLowerCase() !== UserGroupVisibility.Public
	);
}

export function isPublicUserGroupPrincipal(
	principal: Principal
): principal is PublicUserGroupPrincipal {
	return isUserGroupPrincipal(principal) && !isPrivateUserGroupPrincipal(principal);
}

export async function createPrincipal(
	userOrPrincipal: User | Principal,
	stringRids?: string[],
	account?: Account
): Promise<Principal> {
	const user = isUserType(userOrPrincipal)
		? (userOrPrincipal as User)
		: mapPrincipalOfUserTypeToUser(userOrPrincipal as Principal);

	const clientPostRequestPayload: ClientPostRequest = {
		rid: stringRids?.[0] ?? '',
		firstName: user.firstName!,
		lastName: user.lastName!,
		email: user.email!,
	};

	const createdUser = await PermissionsHttp.clientPost(clientPostRequestPayload);
	if (!account) {
		throw new Error('Account is required to map user to principal.');
	}
	return mapUserToPrincipal(account, createdUser);
}
